/**
 * CNC PM PWA - LINE & Webhook HTTP Dispatch Client
 * Handles HTTP transmissions to Webhook endpoints, LINE Messaging API,
 * LINE Notify, and Google Apps Script CORS relay.
 */

import type { LineAnomalyPayload } from '../../types';
import {
  buildLineFlexMessage,
  buildGenericWebhookPayload,
  buildLineNotifyMessage,
  buildGasRelayPayload,
  type LineFlexMessage,
} from './flexMessageBuilder';

export type AlertDispatchMode =
  | 'direct_webhook'
  | 'gas_relay'
  | 'line_messaging_api'
  | 'line_notify';

export interface WebhookClientConfig {
  mode?: AlertDispatchMode;
  webhookUrl?: string;
  apiToken?: string;
  targetId?: string; // LINE User ID 'U...' or Group ID 'C...'
  gasRelayUrl?: string;
  timeoutMs?: number;
}

export interface WebhookClientResponse {
  success: boolean;
  status: number;
  statusText: string;
  messageId?: string;
  responseBody?: any;
  error?: string;
  timestamp: string;
}

/**
 * Dispatches an anomaly alert payload to configured destination
 */
export async function sendWebhookAlert(
  payload: LineAnomalyPayload,
  config: WebhookClientConfig
): Promise<WebhookClientResponse> {
  const timestamp = new Date().toISOString();
  const mode = config.mode || (config.webhookUrl ? 'direct_webhook' : 'line_notify');
  const timeoutMs = config.timeoutMs || (mode === 'gas_relay' ? 25000 : 10000);

  // 1. Check browser network status if available
  if (typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean' && !navigator.onLine) {
    throw new Error('Network error: device is currently offline');
  }

  // 2. Prepare payload and endpoint depending on mode
  const flexMessage = buildLineFlexMessage(payload);

  if (mode === 'gas_relay') {
    const targetUrl = config.gasRelayUrl || config.webhookUrl;
    if (!targetUrl) {
      throw new Error('LINE GAS Relay configuration incomplete: missing Google Apps Script Web App URL');
    }
    if (!config.apiToken) {
      throw new Error('LINE Messaging API Token is required for GAS relay');
    }

    const relayBody = buildGasRelayPayload(
      config.apiToken,
      config.targetId || '',
      flexMessage
    );

    return await executePost(targetUrl, JSON.stringify(relayBody), {
      'Content-Type': 'text/plain;charset=utf-8',
    }, timeoutMs);
  }

  if (mode === 'line_notify') {
    const targetUrl = config.webhookUrl || 'https://notify-api.line.me/api/notify';
    if (!config.apiToken && !config.webhookUrl) {
      throw new Error('LINE configuration incomplete: missing Webhook URL or API Token');
    }

    const textMessage = buildLineNotifyMessage(payload);
    const bodyParams = new URLSearchParams();
    bodyParams.append('message', `\n${textMessage}`);

    const headers: Record<string, string> = {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    };
    if (config.apiToken) {
      headers['Authorization'] = `Bearer ${config.apiToken}`;
    }

    return await executePost(targetUrl, bodyParams.toString(), headers, timeoutMs);
  }

  if (mode === 'line_messaging_api') {
    const targetUrl = config.webhookUrl || 'https://api.line.me/v2/bot/message/push';
    if (!config.apiToken) {
      throw new Error('LINE Messaging API Token is required');
    }
    if (!config.targetId) {
      throw new Error('LINE Target ID (User ID / Group ID) is required');
    }

    const pushBody = {
      to: config.targetId,
      messages: [flexMessage],
    };

    return await executePost(targetUrl, JSON.stringify(pushBody), {
      'Content-Type': 'application/json; charset=UTF-8',
      'Authorization': `Bearer ${config.apiToken}`,
    }, timeoutMs);
  }

  // Default: direct_webhook (Make, n8n, Zapier, Discord, Slack, custom proxy)
  const targetUrl = config.webhookUrl;
  if (!targetUrl) {
    throw new Error('LINE configuration incomplete: missing Webhook URL or API Token');
  }

  // Combine rich representations so external webhooks can ingest any format
  const genericPayload = buildGenericWebhookPayload(payload);
  const webhookBody = {
    ...genericPayload,
    lineFlexMessage: flexMessage,
    rawAnomalyPayload: payload,
  };

  const headers: Record<string, string> = {
    'Content-Type': 'application/json; charset=UTF-8',
  };
  if (config.apiToken) {
    headers['Authorization'] = `Bearer ${config.apiToken}`;
  }

  return await executePost(targetUrl, JSON.stringify(webhookBody), headers, timeoutMs);
}

/**
 * Dispatches a simulated test alert to verify endpoint connectivity
 */
export async function sendTestNotification(
  config: WebhookClientConfig
): Promise<{ success: boolean; message: string; timestamp: string }> {
  if (!config || (!config.webhookUrl && !config.apiToken && !config.gasRelayUrl)) {
    throw new Error('LINE configuration incomplete: missing Webhook URL or API Token');
  }

  const testPayload: LineAnomalyPayload = {
    machineId: 'CNC-TEST-01',
    machineName: 'TOS VARNSDORF WHN(Q) 13/15 (Simulated Test)',
    inspectorName: 'Admin / System Test',
    inspectionDate: new Date().toISOString(),
    operatingHours: 1850.5,
    overallStatus: 'DEFECT_FOUND',
    defects: [
      {
        itemNo: 4,
        task: 'ตรวจสอบแรงดันระบบไฮดรอลิก (Hydraulic System Pressure) [TEST]',
        criteria: 'ต้องไม่ต่ำกว่า 6.8 MPa (68 bar)',
        measuredValue: '6.4 MPa (Out of Tolerance)',
        status: 'NG',
        notes: 'Simulated alert verifying LINE Webhook connectivity from CNC PM PWA.',
      },
    ],
    recordUrl: typeof window !== 'undefined' ? window.location.href : 'https://pwa.factory.local/records/TEST-001',
  };

  const result = await sendWebhookAlert(testPayload, config);
  if (!result.success) {
    throw new Error(result.error || `HTTP error ${result.status}: ${result.statusText}`);
  }

  return {
    success: true,
    message: 'LINE test notification successfully dispatched',
    timestamp: result.timestamp,
  };
}

/**
 * Internal helper to execute fetch with timeout and error capture
 */
async function executePost(
  url: string,
  body: string,
  headers: Record<string, string>,
  timeoutMs: number
): Promise<WebhookClientResponse> {
  const timestamp = new Date().toISOString();

  // Validate URL protocol
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      throw new Error(`Invalid URL protocol: '${parsed.protocol}'. Expected 'https:' or 'http:'`);
    }
  } catch (err: any) {
    throw new Error(`Invalid Webhook URL '${url}': ${err.message}`);
  }

  let controller: AbortController | null = null;
  let timeoutId: any = null;

  if (typeof AbortController !== 'undefined') {
    controller = new AbortController();
    timeoutId = setTimeout(() => controller?.abort(), timeoutMs);
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body,
      signal: controller ? controller.signal : undefined,
    });

    if (timeoutId) clearTimeout(timeoutId);

    let parsedResponse: any = null;
    const text = await response.text();
    try {
      parsedResponse = JSON.parse(text);
    } catch {
      parsedResponse = text;
    }

    if (!response.ok) {
      return {
        success: false,
        status: response.status,
        statusText: response.statusText,
        error: `HTTP ${response.status}: ${response.statusText} — ${typeof parsedResponse === 'string' ? parsedResponse : JSON.stringify(parsedResponse)}`,
        responseBody: parsedResponse,
        timestamp,
      };
    }

    if (parsedResponse && typeof parsedResponse === 'object' && parsedResponse.success === false) {
      return {
        success: false,
        status: parsedResponse.statusCode || 400,
        statusText: parsedResponse.code || parsedResponse.status || 'ERROR',
        error: parsedResponse.message || (parsedResponse.lineResponse ? (typeof parsedResponse.lineResponse === 'string' ? parsedResponse.lineResponse : JSON.stringify(parsedResponse.lineResponse)) : 'Relay request failed'),
        responseBody: parsedResponse,
        timestamp,
      };
    }

    return {
      success: true,
      status: response.status,
      statusText: response.statusText,
      messageId: parsedResponse?.messageId || `msg_${Date.now()}`,
      responseBody: parsedResponse,
      timestamp,
    };
  } catch (err: any) {
    if (timeoutId) clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      throw new Error(`การเชื่อมต่อหมดเวลา (Timeout ${timeoutMs / 1000} วินาที) เซิร์ฟเวอร์ GAS อาจกำลังเริ่มต้นทำงาน กรุณาลองกดทดสอบใหม่อีกครั้ง`);
    }
    if ((err.message?.includes('Failed to fetch') || err.name === 'TypeError') && url.includes('api.line.me')) {
      throw new Error(`LINE API ปฏิเสธการส่งตรงจากเบราว์เซอร์ (ติดข้อจำกัด CORS ของ LINE) กรุณาเลือกโหมด "GAS CORS Relay" เพื่อส่งผ่าน Google Apps Script แทน`);
    }
    if ((err.message?.includes('Failed to fetch') || err.name === 'TypeError') && url.includes('script.google.com')) {
      throw new Error(`ไม่สามารถเชื่อมต่อไปยัง Google Apps Script ได้ (Failed to fetch) กรุณาตรวจสอบว่า URL เว็บแอปถูกต้อง ไม่ตกหล่น และกำหนดสิทธิ์การเข้าถึงเป็น "Anyone" (ทุกคน)`);
    }
    throw new Error(`Network error: ${err.message || 'failed to connect to LINE API endpoint'}`);
  }
}
